import { Link } from "react-router";

type SponsorshipsSectionProps = {
  email?: string;
  mediaKitUrl?: string;
};

export default function SponsorshipsSection({
  email = "hello@jaseowns.com",
  mediaKitUrl = "/media-kit.pdf",
}: SponsorshipsSectionProps) {
  const opportunities = [
    {
      label: "Sponsored videos & integrations",
      desc: "Product demos, tutorials, or mentions in YouTube content.",
    },
    {
      label: "Live stream partnerships",
      desc: "Branded overlays, shoutouts, and sponsored segments.",
    },
    {
      label: "Product placements & reviews",
      desc: "Honest, in-depth coverage of relevant tools and gear.",
    },
    {
      label: "Custom collaborations",
      desc: "Unique campaigns crafted to connect with my community.",
    },
  ];

  const reasons = [
    "Engaged audience: developers + gamers across YouTube, Twitch & Discord.",
    "Authentic content: trusted recommendations, not cookie-cutter ads.",
    "Professional creator: consistent output in gaming & dev niches.",
  ];

  return (
    <section
      className="relative py-16 px-0 sm:px-4 lg:px-8"
      aria-labelledby="sponsorships-title"
    >
      <div className="mx-auto max-w-5xl bg-slate-900/70 rounded-3xl">
        <div className="relative overflow-hidden sm:rounded-3xl sm:border sm:border-white/10 sm:bg-slate-900/70 sm:shadow-2xl">
          {/* Accent Ring */}
          <div className="pointer-events-none absolute -inset-1 rounded-3xl bg-gradient-to-tr from-emerald-500/20 via-teal-500/10 to-cyan-500/20 blur-2xl" />

          <div className="relative z-10 grid gap-10 p-8 sm:p-12 lg:grid-cols-5 lg:gap-12">
            {/* Intro */}
            <div className="lg:col-span-3">
              <h2
                id="sponsorships-title"
                className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl"
              >
                <span className="bg-gradient-to-r from-emerald-300 via-teal-300 to-cyan-300 bg-clip-text text-transparent">
                  Sponsorships & Partnerships
                </span>
              </h2>

              <p className="mt-4 text-slate-300 leading-relaxed">
                I collaborate with brands and creators who want to reach an
                audience of{" "}
                <span className="font-semibold text-white">
                  developers, gamers, and tech enthusiasts
                </span>
                . Through YouTube videos, live streams, and community projects,
                I deliver authentic content that engages viewers while
                showcasing tools, products, and platforms that help them
                succeed.
              </p>

              {/* Opportunities */}
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {opportunities.map((item) => (
                  <div key={item.label} className="flex items-start gap-3">
                    <CheckIcon className="mt-1 h-5 w-5 flex-none text-emerald-400" />
                    <div>
                      <p className="text-slate-200 font-medium">{item.label}</p>
                      <p className="text-slate-400 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Why partner */}
              <div className="mt-8 rounded-2xl border border-white/10 bg-slate-800/50 p-5">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400">
                  Why partner with me
                </h3>
                <ul className="mt-3 space-y-2">
                  {reasons.map((point) => (
                    <li key={point} className="flex items-start gap-3">
                      <SparkleIcon className="mt-1 h-5 w-5 flex-none text-cyan-400" />
                      <span className="text-slate-200">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA buttons */}
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link
                  to={`mailto:${email}?subject=Sponsorship%20inquiry`}
                  className="w-full sm:w-auto inline-flex items-center justify-center rounded-xl bg-emerald-400 px-5 py-3 text-sm font-semibold text-slate-900 shadow-lg shadow-emerald-400/20 transition hover:translate-y-[-1px] hover:bg-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:ring-offset-2 focus:ring-offset-slate-900"
                >
                  <MailIcon className="mr-2 h-5 w-5" /> Contact Me
                </Link>
                {/* <Link
                  to={mediaKitUrl}
                  className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-white backdrop-blur transition hover:translate-y-[-1px] hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/20 focus:ring-offset-2 focus:ring-offset-slate-900"
                >
                  <ExternalIcon className="mr-2 h-5 w-5" /> Request Media Kit
                </Link> */}
              </div>
            </div>

            {/* Side panel */}
            <aside className="lg:col-span-2">
              <div className="h-full sm:rounded-2xl sm:border sm:border-white/10 sm:bg-slate-800/40 sm:p-6">
                <h3 className="text-base font-semibold text-white">
                  Audience Snapshot
                </h3>
                <ul className="mt-4 space-y-3">
                  {[
                    {
                      label: "YouTube",
                      desc: "Gaming + dev tutorials, growing subscriber base.",
                    },
                    {
                      label: "Twitch",
                      desc: "Live gaming, coding, and interactive streams.",
                    },
                    {
                      label: "Discord",
                      desc: "Engaged dev + gamer community hub.",
                    },
                    {
                        label: "Ultima Online",
                        desc: "Passionate UO community builder and script developer.",
                    },
                    {
                        label: "Podcast",
                        desc: "Podcast guest appearances",
                    },
                  ].map((item) => (
                    <li
                      key={item.label}
                      className="rounded-xl border border-white/10 bg-slate-900/40 p-4"
                    >
                      <p className="font-medium text-slate-100">{item.label}</p>
                      <p className="mt-1 text-sm text-slate-300">{item.desc}</p>
                    </li>
                  ))}
                </ul>

                <p className="my-4 font-semibold text-white">
                  <Link
                    to={`mailto:${email}?subject=Sponsorship%20inquiry`}
                    className="w-full sm:w-auto hover:underline"
                  >
                    Have something to promote? Let's connect!
                  </Link>
                </p>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </section>
  );
}

// Icons (reuse from your other section)
function CheckIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M9 12.75l2 2 4-4.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeWidth="1.2"
        opacity=".35"
      />
    </svg>
  );
}

function SparkleIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M12 3l1.6 3.8L17 8.4l-3.4 1.6L12 14l-1.6-4-3.4-1.6 3.4-1.6L12 3z"
        stroke="currentColor"
        strokeWidth="1.2"
        fill="currentColor"
        opacity=".25"
      />
      <path
        d="M6 18l.8 1.8L8.6 21l-1.8.8L6 24l-.8-2.2L3.4 21l1.8-.8L6 18z"
        fill="currentColor"
        opacity=".35"
      />
    </svg>
  );
}

function MailIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M3 6.75A1.75 1.75 0 014.75 5h14.5A1.75 1.75 0 0121 6.75V17a2 2 0 01-2 2H5a2 2 0 01-2-2V6.75z"
        stroke="currentColor"
        strokeWidth="1.3"
      />
      <path
        d="M4 7l8 5.5L20 7"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
    </svg>
  );
}
